import React from "react";
import { motion } from "framer-motion";
import {
  Code,
  Users,
  ArrowRight,
  Smartphone,
  Monitor,
  Cloud,
  Briefcase,
  Target,
  CheckCircle,
  Zap,
  Award,
  TrendingUp,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import "./Services.scss";

const ServicesIndex: React.FC = () => {
  const navigate = useNavigate();

  // Hero Section
  const HeroSection: React.FC = () => {
    return (
      <section className="sd_service-hero">
        <div className="container">
          <motion.div
            className="sd_service-hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.div
              className="sd_service-badge"
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              🚀 Our Services
            </motion.div>

            <h1 className="sd_service-hero-title">
              Comprehensive Solutions for{" "}
              <span className="gradient-text">Business Growth</span>
            </h1>
            <p className="sd_service-hero-subtitle">
              From cutting-edge application development to strategic staffing
              solutions, we provide end-to-end services to accelerate your
              business success.
            </p>

            <motion.div
              className="sd_service-hero-stats"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <div className="stat-box">
                <span className="stat-number">500+</span>
                <span className="stat-label">Projects Delivered</span>
              </div>
              <div className="stat-box">
                <span className="stat-number">5000+</span>
                <span className="stat-label">Placements Made</span>
              </div>
              <div className="stat-box">
                <span className="stat-number">98%</span>
                <span className="stat-label">Client Satisfaction</span>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>
    );
  };

  // Main Services Section
  const MainServices: React.FC = () => {
    const services = [
      {
        id: "app-dev",
        icon: <Code size={48} />,
        title: "Application Development",
        subtitle: "Custom Software Solutions",
        description:
          "Transform your ideas into powerful, scalable applications. We specialize in web, mobile, and desktop development using cutting-edge technologies.",
        features: [
          {
            icon: <Monitor size={20} />,
            text: "Web Application Development",
          },
          {
            icon: <Smartphone size={20} />,
            text: "Mobile App Development",
          },
          {
            icon: <Code size={20} />,
            text: "Desktop Applications",
          },
          {
            icon: <Cloud size={20} />,
            text: "Cloud-Based Solutions",
          },
        ],
        stats: [
          { label: "Projects", value: "500+" },
          { label: "Technologies", value: "20+" },
          { label: "Success Rate", value: "98%" },
        ],
        color: "#4A90E2",
        path: "/services/application-development",
      },
      {
        id: "staffing",
        icon: <Users size={48} />,
        title: "Staffing Solutions",
        subtitle: "Strategic Talent Acquisition",
        description:
          "Connect with top-tier talent or find your dream job. Our comprehensive staffing services cover IT, contract, permanent, and executive placements.",
        features: [
          {
            icon: <Briefcase size={20} />,
            text: "IT Staffing & Recruitment",
          },
          {
            icon: <Target size={20} />,
            text: "Contract & Permanent Roles",
          },
          {
            icon: <Users size={20} />,
            text: "Executive Search",
          },
          {
            icon: <Award size={20} />,
            text: "Managed Workforce Solutions",
          },
        ],
        stats: [
          { label: "Placements", value: "5000+" },
          { label: "Industries", value: "15+" },
          { label: "Match Rate", value: "95%" },
        ],
        color: "#50C878",
        path: "/services/staffing-solutions",
      },
    ];

    return (
      <section className="sd_service-main-section">
        <div className="container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title">What We Offer</h2>
            <p className="section-subtitle">
              Two core service lines designed to power your business forward
            </p>
          </motion.div>

          <div className="sd_service-main-grid">
            {services.map((service, idx) => (
              <motion.div
                key={service.id}
                className="sd_service-main-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.2 }}
              >
                <div className="service-header">
                  <div
                    className="service-icon-large"
                    style={{ color: service.color }}
                  >
                    {service.icon}
                  </div>
                  <div className="service-header-text">
                    <h3 className="service-title">{service.title}</h3>
                    <p className="service-subtitle">{service.subtitle}</p>
                  </div>
                </div>

                <p className="service-description">{service.description}</p>

                <div className="service-features-list">
                  {service.features.map((feature, fIdx) => (
                    <div key={fIdx} className="feature-item-main">
                      <span
                        className="feature-icon"
                        style={{ color: service.color }}
                      >
                        {feature.icon}
                      </span>
                      <span className="feature-text">{feature.text}</span>
                    </div>
                  ))}
                </div>

                <div className="service-stats-row">
                  {service.stats.map((stat, sIdx) => (
                    <div key={sIdx} className="stat-item-main">
                      <span className="stat-value">{stat.value}</span>
                      <span className="stat-label-main">{stat.label}</span>
                    </div>
                  ))}
                </div>

                <button
                  className="btn btn-primary btn-block"
                  onClick={() => navigate(service.path)}
                >
                  Learn More <ArrowRight size={20} />
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    );
  };

  // Why Choose Both Section
  const WhyChooseBoth: React.FC = () => {
    const benefits = [
      {
        icon: <Zap size={32} />,
        title: "End-to-End Solutions",
        description:
          "Get both development and staffing services under one roof for seamless project execution",
      },
      {
        icon: <Award size={32} />,
        title: "Industry Expertise",
        description:
          "15+ years of combined experience in technology and talent acquisition",
      },
      {
        icon: <TrendingUp size={32} />,
        title: "Scalable Services",
        description:
          "Solutions that grow with your business, from startup to enterprise",
      },
      {
        icon: <CheckCircle size={32} />,
        title: "Proven Track Record",
        description:
          "Thousands of successful projects and placements with 98% satisfaction rate",
      },
    ];

    return (
      <section className="sd_service-benefits-section">
        <div className="container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title">Why Choose Our Services?</h2>
            <p className="section-subtitle">
              Comprehensive solutions backed by expertise and results
            </p>
          </motion.div>

          <div className="sd_service-benefits-grid">
            {benefits.map((benefit, idx) => (
              <motion.div
                key={idx}
                className="sd_service-benefit-card"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                whileHover={{ scale: 1.05 }}
              >
                <div className="benefit-icon">{benefit.icon}</div>
                <h3 className="benefit-title">{benefit.title}</h3>
                <p className="benefit-description">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    );
  };

  // CTA Section
  const CTASection: React.FC = () => {
    return (
      <section className="sd_service-cta-section">
        <div className="container">
          <motion.div
            className="sd_service-cta-content"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="cta-title">Ready to Get Started?</h2>
            <p className="cta-text">
              Let's discuss how our services can help achieve your business
              goals. Contact us today for a free consultation.
            </p>
            <div className="cta-buttons">
              <button
                className="btn btn-primary"
                onClick={() => navigate("/contact-us")}
              >
                Contact Us <ArrowRight size={20} />
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => navigate("/about-us")}
              >
                Learn More About Us
              </button>
            </div>
          </motion.div>
        </div>
      </section>
    );
  };

  return (
    <div className="sd_service-container">
      <HeroSection />
      <MainServices />
      <WhyChooseBoth />
      <CTASection />
    </div>
  );
};

export default ServicesIndex;
